import addCalendarEventsToCache from '../store/apis/addCalendarEventsToCache';
import getCalendarEventsFromServer from '../services/getCalendarEventsFromServer';
import getDateRange from '../store/apis/getDateRange';
import { Calendar } from 'mini-event-to-calendar-association/lib/schema/Calendar';
import { CalendarEvent } from 'mini-calendar-event-types';
import { convertDateRangeToDisplayDateRange } from '../utils/epochTimeToDisplayDate';
import { getAndUpdateActualFolderId } from 'mini-calendar-store/lib/utils/getAndUpdateActualFolderId';
import { getCalendarInfoStore } from '../store/store';
import { traceAndReject } from 'error-handling';
import { addCalendarFolderIdToLocalStorage } from './selectedCalendarsLocalStorageUtils';

export enum AddCalendarResult {
    DATERANGE_NOT_SET,
    CALENDAR_NOT_ADDED,
    CALENDAR_ADDED,
}

/**
 * Subscribe to the calendar events and fetch the events when date range is existing.
 * @param calendar - calendar to add.
 * @returns Promise rejected when fetch fails.
 */
export default async function addCalendar(
    calendar: Calendar,
    doNotFetchEvents?: boolean
): Promise<AddCalendarResult> {
    // When we fetch the calendars using getCalendarFoldersService, some of the calendars (e.g. linked calendars)
    // have invalid folder ids. An extra service call must be made to get the right folder id and update the same.
    // The calendar folder id needs to be updated when we want to fetch events for a calendar where id is invalid like linked calendars.
    if (!calendar.isFolderIDValid) {
        await getAndUpdateActualFolderId(calendar);
    }

    // Addition to be done after the update of actual folder id
    calendar.isFolderIDValid && addCalendarFolderIdToLocalStorage(calendar.calculatedFolderId);

    const dateRange = getDateRange();
    getCalendarInfoStore().addCalendarInfo(calendar);

    if (doNotFetchEvents) {
        return AddCalendarResult.CALENDAR_ADDED;
    }

    if (!dateRange) {
        return AddCalendarResult.DATERANGE_NOT_SET;
    }

    // pull calendar events and update events store.
    try {
        const events: CalendarEvent[] = await getCalendarEventsFromServer(
            calendar,
            convertDateRangeToDisplayDateRange(dateRange)
        );

        // Add only if calendar still present in calendar info store.
        const areEventsToBeAdded = !!getCalendarInfoStore().getCalendarInfo(
            calendar.calculatedFolderId
        );
        // If calendar no longer present like, if user unselected
        // while add call in progress then no point adding the events now
        if (areEventsToBeAdded) {
            addCalendarEventsToCache(events);
        }

        return Promise.resolve(
            areEventsToBeAdded
                ? AddCalendarResult.CALENDAR_ADDED
                : AddCalendarResult.CALENDAR_NOT_ADDED
        );
    } catch (error) {
        return traceAndReject('addCalendar', error);
    }
}
